import type React from 'react';
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { formatEuro } from '../../utils/currency';

interface OverallBudgetChartProps {
  totalBudgetedIncome: number;
  totalActualIncome: number;
  totalBudgetedExpenses: number;
  totalActualExpenses: number;
  taxNet?: number;
  actualCashFlow?: number;
}

const OverallBudgetChart: React.FC<OverallBudgetChartProps> = ({
  totalBudgetedIncome,
  totalActualIncome,
  totalBudgetedExpenses,
  totalActualExpenses,
  taxNet,
  actualCashFlow,
}) => {
  // Prepare chart data
  const chartData = [
    {
      category: 'Income',
      budgeted: totalBudgetedIncome,
      actual: totalActualIncome,
    },
    {
      category: 'Expenses',
      budgeted: totalBudgetedExpenses,
      actual: totalActualExpenses,
    },
  ];

  // Custom tooltip
  const CustomTooltip = ({
    active,
    payload,
    label,
  }: {
    active?: boolean;
    payload?: { value: number }[];
    label?: string;
  }) => {
    if (active && payload?.length) {
      return (
        <div className="bg-white p-3 border border-gray-300 rounded shadow-lg">
          <p className="font-medium text-gray-900">{label}</p>
          <p className="text-sm text-blue-600">
            <span>Budgeted:</span> {formatEuro(payload[0]?.value ?? 0)}
          </p>
          <p className="text-sm text-green-600">
            <span>Actual:</span> {formatEuro(payload[1]?.value ?? 0)}
          </p>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="w-full">
      <h3 className="text-lg font-medium text-gray-900 mb-4">Overall Budget vs Actual</h3>
      <ResponsiveContainer width="100%" height={300}>
        <BarChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="category" />
          <YAxis tickFormatter={(value) => `€${(value / 1000).toFixed(0)}K`} />
          <Tooltip content={<CustomTooltip />} />
          <Bar dataKey="budgeted" fill="#3B82F6" name="Budgeted" />
          <Bar dataKey="actual" fill="#10B981" name="Actual" />
        </BarChart>
      </ResponsiveContainer>

      {/* Summary numbers below chart */}
      <div className="mt-4 grid grid-cols-2 gap-4 text-sm">
        <div className="text-center">
          <div className="font-medium text-gray-900">Income</div>
          <div className="text-blue-600">Budget: {formatEuro(totalBudgetedIncome)}</div>
          <div className="text-green-600">Actual: {formatEuro(totalActualIncome)}</div>
          <div
            className={`font-medium ${
              totalActualIncome - totalBudgetedIncome >= 0 ? 'text-green-600' : 'text-red-600'
            }`}
          >
            Variance: {totalActualIncome - totalBudgetedIncome >= 0 ? '+' : ''}
            {formatEuro(Math.abs(totalActualIncome - totalBudgetedIncome))}
          </div>
        </div>
        <div className="text-center">
          <div className="font-medium text-gray-900">Expenses</div>
          <div className="text-blue-600">Budget: {formatEuro(totalBudgetedExpenses)}</div>
          <div className="text-orange-600">Actual: {formatEuro(totalActualExpenses)}</div>
          <div
            className={`font-medium ${
              totalBudgetedExpenses - totalActualExpenses >= 0 ? 'text-green-600' : 'text-red-600'
            }`}
          >
            Variance: {totalBudgetedExpenses - totalActualExpenses >= 0 ? '+' : ''}
            {formatEuro(Math.abs(totalBudgetedExpenses - totalActualExpenses))}
          </div>
        </div>
      </div>

      {/* Operational result + optional tax reconciliation */}
      <div className="mt-4 pt-4 border-t border-gray-200">
        {taxNet !== undefined && taxNet !== 0 && actualCashFlow !== undefined ? (
          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-gray-500">Operational result (ex. VAT &amp; taxes)</span>
              <span
                className={`font-medium ${totalActualIncome - totalActualExpenses >= 0 ? 'text-green-600' : 'text-red-600'}`}
              >
                {totalActualIncome - totalActualExpenses >= 0 ? '+' : ''}
                {formatEuro(totalActualIncome - totalActualExpenses)}
              </span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-500">Tax payments (verot)</span>
              <span className={`font-medium ${taxNet >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                {taxNet >= 0 ? '+' : ''}
                {formatEuro(taxNet)}
              </span>
            </div>
            <div className="flex justify-between text-sm pt-2 border-t border-gray-100">
              <span className="font-semibold text-gray-900">Actual cash flow</span>
              <span
                className={`font-bold ${actualCashFlow >= 0 ? 'text-green-700' : 'text-red-700'}`}
              >
                {actualCashFlow >= 0 ? '+' : ''}
                {formatEuro(actualCashFlow)}
              </span>
            </div>
          </div>
        ) : (
          <div className="text-center">
            <div className="font-medium text-gray-900 mb-2">Operational result (ex. VAT)</div>
            <div
              className={`text-lg font-bold ${
                totalActualIncome - totalActualExpenses >= 0 ? 'text-green-600' : 'text-red-600'
              }`}
            >
              {totalActualIncome - totalActualExpenses >= 0 ? '+' : ''}
              {formatEuro(Math.abs(totalActualIncome - totalActualExpenses))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default OverallBudgetChart;
